import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Modal,
  TextInput,
  Select,
  Alert,
  Badge,
  Button,
  Mail01,
  Phone01,
  Building07,
} from '@opendate-app/setlist';
import useAutoSave from '../../shared/components/useAutoSave';
import { deriveNoteTitle } from '../../shared/components/CrmNotesCard';
import CollapsibleSection from '../../shared/components/CollapsibleSection';
import CrmCustomFieldsSection from '../../shared/components/CrmCustomFieldsSection';
import orgSourceOptions from '../../shared/constants/orgSourceOptions';
import { COUNTRY_OPTIONS, getRegionsForCountry, getRegionLabelForCountry } from '../../CustomFieldValues/constants/customFieldValuesConstants';
import { canSubmitCustomFieldValue } from '../../CustomFieldValues/actions/customFieldValuesActionCreators';
import useCustomFieldValues from '../../shared/hooks/useCustomFieldValues';
import crmLabels from '../../shared/utils/crmLabels';

const CONTACT_DETAILS_ICON = '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>';
const SOCIAL_LINK_ICON = '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>';
const NOTES_ICON = '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>';

function OrganizationFormModal({
  isOpen,
  onClose,
  organization,
  team,
  csrfToken,
  customFieldDefinitions = [],
  onSaved,
}) {
  const isEditing = !!(organization && organization.id);
  const labels = crmLabels(team);
  const orgSingular = labels.organization.singular;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('');
  const [facebookUrl, setFacebookUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [orgType, setOrgType] = useState('');
  const [industry, setIndustry] = useState('');
  const [source, setSource] = useState('');
  const [notes, setNotes] = useState('');
  const { customFieldValues, handleCustomFieldChange, buildCustomFieldAttributes, resetCustomFieldValues, syncCustomFieldRowIds } = useCustomFieldValues(customFieldDefinitions, organization ? organization.custom_field_values : []);
  const [filterValues, setFilterValues] = useState({ org_types: [], industries: [] });
  const pendingNoteRef = useRef('');

  const handleSaved = useCallback(async (saved, isNew) => {
    syncCustomFieldRowIds(saved.custom_field_values);
    const noteBody = pendingNoteRef.current;
    pendingNoteRef.current = '';
    if (noteBody && saved && saved.id) {
      try {
        if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        await axios.post(`/teams/${team.id}/crm/notes.json`, {
          note: { title: deriveNoteTitle(noteBody), body: noteBody, notable_type: 'Organization', notable_id: saved.id },
        });
        setNotes((current) => (current === noteBody ? '' : current));
      } catch (e) {
        toast.error(`${orgSingular} saved, but the note could not be saved. Click Save again to retry.`);
        if (onSaved) onSaved(saved, isNew);
        return false;
      }
    }
    if (onSaved) onSaved(saved, isNew);
  }, [onSaved, team, csrfToken, syncCustomFieldRowIds, orgSingular]);

  const { recordId, saveStatus, errors, saveFields } = useAutoSave({
    team,
    csrfToken,
    resourcePath: 'crm/organizations',
    resourceKey: 'organization',
    existingId: isEditing ? organization.id : null,
    isOpen,
    onSaved: handleSaved,
  });

  useEffect(() => {
    if (!isOpen) return;

    // The modal stays mounted across open/close; re-init custom field state so
    // multi-association row ids don't go stale after a save.
    resetCustomFieldValues(organization && organization.id ? organization.custom_field_values : []);
    pendingNoteRef.current = '';

    if (organization && organization.id) {
      setName(organization.name || '');
      setEmail(organization.email || '');
      setPhone(organization.phone || '');
      setWebsiteUrl(organization.website_url || '');
      setCity(organization.city || '');
      setState(organization.state || '');
      setZip(organization.zip || '');
      setCountry(organization.country || '');
      setFacebookUrl(organization.facebook_url || '');
      setInstagramUrl(organization.instagram_url || '');
      setTwitterUrl(organization.twitter_url || '');
      setLinkedinUrl(organization.linkedin_url || '');
      setAddress1(organization.address_1 || '');
      setAddress2(organization.address_2 || '');
      setOrgType(organization.org_type || '');
      setIndustry(organization.industry || '');
      setSource(organization.source || '');
      setNotes('');
    } else {
      setName((organization && organization.name) || '');
      setEmail('');
      setPhone('');
      setWebsiteUrl('');
      setCity('');
      setState('');
      setZip('');
      setCountry('');
      setFacebookUrl('');
      setInstagramUrl('');
      setTwitterUrl('');
      setLinkedinUrl('');
      setAddress1('');
      setAddress2('');
      setOrgType('');
      setIndustry('');
      setSource('');
      setNotes('');
    }
  }, [isOpen, organization]);

  useEffect(() => {
    if (!isOpen || !team) return;
    axios.get(`/teams/${team.id}/crm/organizations/filter_values`)
      .then((res) => setFilterValues(res.data || {}))
      .catch(() => {});
  }, [isOpen, team]);

  if (!isOpen) return null;

  const canSubmitForm = name.trim().length > 0 && customFieldValues.every((cfv) => canSubmitCustomFieldValue(cfv));

  const buildPayload = () => ({
    name,
    email,
    phone,
    website_url: websiteUrl,
    city,
    state,
    zip,
    country,
    facebook_url: facebookUrl,
    instagram_url: instagramUrl,
    twitter_url: twitterUrl,
    linkedin_url: linkedinUrl,
    address_1: address1,
    address_2: address2,
    org_type: orgType,
    industry,
    source,
    custom_field_values_attributes: buildCustomFieldAttributes(),
  });

  const handleClose = () => {
    if (saveStatus !== 'saving') onClose();
  };

  const handleSubmit = () => {
    if (!canSubmitForm || saveStatus === 'saving') return;
    pendingNoteRef.current = notes.trim();
    saveFields(buildPayload(), { afterSave: onClose });
  };

  const saveStatusLabel = {
    idle: null,
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Error saving',
  }[saveStatus];

  const saveStatusColor = {
    saving: 'gray',
    saved: 'success',
    error: 'error',
  }[saveStatus] || 'gray';

  const breadcrumbLabel = name.trim() ? name.trim() : (isEditing ? `Edit ${orgSingular}` : `Add ${orgSingular}`);

  const regionLabel = getRegionLabelForCountry(country || 'US');

  const footer = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
      {saveStatusLabel && <Badge color={saveStatusColor}>{saveStatusLabel}</Badge>}
      <Button
        variant="primary"
        disabled={!canSubmitForm || saveStatus === 'saving'}
        onClick={handleSubmit}
      >
        {saveStatus === 'saving' ? 'Saving…' : `Save ${orgSingular}`}
      </Button>
    </div>
  );

  return (
    <Modal
      open
      onClose={handleClose}
      icon={false}
      layout="stacked"
      width={920}
      title={breadcrumbLabel}
      footer={footer}
    >
      {errors.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Alert color="error" title="Please fix the following" description={errors.join(', ')} />
        </div>
      )}

      <div className="cfm-body" style={{ maxHeight: '68vh' }}>
        <div className="cfm-pane-main">
          <div className="cfm-field">
            <TextInput
              label="Name"
              required
              leadingIcon={Building07}
              placeholder={`${orgSingular} name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="cfm-field-row-2col">
            <div className="cfm-field">
              <TextInput
                label="Email"
                type="email"
                leadingIcon={Mail01}
                placeholder="info@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="cfm-field">
              <TextInput
                label="Phone"
                type="tel"
                leadingIcon={Phone01}
                placeholder="+1 (555) 000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          <div className="cfm-field">
            <TextInput
              label="Website"
              type="url"
              placeholder="https://example.com"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </div>

          <CollapsibleSection title="Contact Details" icon={CONTACT_DETAILS_ICON} defaultExpanded={false}>
            <div className="cfm-field-row-2col">
              <div className="cfm-field">
                <TextInput
                  label="Address 1"
                  placeholder="Street address"
                  value={address1}
                  onChange={(e) => setAddress1(e.target.value)}
                />
              </div>
              <div className="cfm-field">
                <TextInput
                  label="Address 2"
                  placeholder="Apt, suite, etc."
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                />
              </div>
            </div>

            <div className="cfm-field">
              <label className="cfm-label">Country</label>
              <Select
                aria-label="Country"
                placeholder="Country"
                clearable
                options={COUNTRY_OPTIONS.map((c) => ({ value: c.code, label: c.name }))}
                value={country || null}
                onChange={(value) => { setCountry(value || ''); setState(''); }}
              />
            </div>

            <div className="cfm-field-row-3col">
              <div className="cfm-field">
                <TextInput
                  label="City"
                  placeholder="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
              <div className="cfm-field">
                <label className="cfm-label">{regionLabel}</label>
                <Select
                  aria-label={regionLabel}
                  placeholder={regionLabel}
                  clearable
                  options={getRegionsForCountry(country || 'US').map((r) => ({ value: r.code, label: r.name }))}
                  value={state || null}
                  onChange={(value) => setState(value || '')}
                />
              </div>
              <div className="cfm-field">
                <TextInput
                  label="Zip"
                  placeholder="Zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </div>
            </div>
          </CollapsibleSection>

          <CrmCustomFieldsSection
            team={team}
            customFieldDefinitions={customFieldDefinitions}
            customFieldValues={customFieldValues}
            onCustomFieldChange={handleCustomFieldChange}
          />

          <CollapsibleSection title="Social Profiles" icon={SOCIAL_LINK_ICON} defaultExpanded={false}>
            <div className="cfm-social-grid">
              <div className="cfm-field">
                <TextInput label="Facebook" type="url" placeholder="facebook.com/page" value={facebookUrl} onChange={(e) => setFacebookUrl(e.target.value)} />
              </div>
              <div className="cfm-field">
                <TextInput label="Instagram" type="url" placeholder="instagram.com/page" value={instagramUrl} onChange={(e) => setInstagramUrl(e.target.value)} />
              </div>
              <div className="cfm-field">
                <TextInput label="X / Twitter" type="url" placeholder="x.com/page" value={twitterUrl} onChange={(e) => setTwitterUrl(e.target.value)} />
              </div>
              <div className="cfm-field">
                <TextInput label="LinkedIn" type="url" placeholder="linkedin.com/company/page" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} />
              </div>
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Notes" icon={NOTES_ICON} defaultExpanded={false}>
            <div className="cfm-field">
              <TextInput
                label="Notes"
                multiline
                rows={3}
                placeholder={`Add a note about this ${orgSingular.toLowerCase()}…`}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </CollapsibleSection>
        </div>

        <div className="cfm-pane-sidebar">
          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">{`${orgSingular} type`}</span>
            <TextInput
              aria-label={`${orgSingular} type`}
              list="org-type-options"
              placeholder="Select or type…"
              value={orgType}
              onChange={(e) => setOrgType(e.target.value)}
              onBlur={() => { if (isEditing || recordId) saveFields({ ...buildPayload(), org_type: orgType }); }}
            />
            <datalist id="org-type-options">
              {(filterValues.org_types || []).map((opt) => <option key={opt} value={opt} />)}
            </datalist>
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Industry</span>
            <TextInput
              aria-label="Industry"
              list="industry-options"
              placeholder="Select or type…"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              onBlur={() => { if (isEditing || recordId) saveFields({ ...buildPayload(), industry }); }}
            />
            <datalist id="industry-options">
              {(filterValues.industries || []).map((opt) => <option key={opt} value={opt} />)}
            </datalist>
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Source</span>
            <Select
              aria-label="Source"
              placeholder="Select…"
              clearable
              options={orgSourceOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
              value={source || null}
              onChange={(value) => {
                const v = value || '';
                setSource(v);
                if (isEditing || recordId) saveFields({ ...buildPayload(), source: v });
              }}
            />
          </div>

          <div className="cfm-sidebar-divider" />
          <div className="cfm-sidebar-section-label">Record Info</div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Created</span>
            <span className="cfm-prop-value-static">
              {organization && organization.created_at ? new Date(organization.created_at).toLocaleDateString() : 'Now'}
            </span>
          </div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Created by</span>
            <span className="cfm-prop-value-static">You</span>
          </div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Modified</span>
            <span className="cfm-prop-value-static">
              {organization && organization.updated_at ? new Date(organization.updated_at).toLocaleDateString() : 'Now'}
            </span>
          </div>

          <div className="cfm-prop-row">
            <span className="cfm-prop-label">Contacts</span>
            <span className="cfm-prop-value-static">{(organization && organization.contacts_count) || 0}</span>
          </div>

          <div className="cfm-prop-row cfm-prop-row--stacked">
            <span className="cfm-prop-label">Status</span>
            <Select aria-label="Status" disabled options={[{ value: 'active', label: 'Active' }]} value="active" />
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default OrganizationFormModal;
